import type { ForwardRefRenderFunction, ReactNode } from "react";
import type React from "react";
import { useState, useRef, forwardRef, useImperativeHandle } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import classNames from "classnames";
import _ from "lodash";
import styled, { css } from "styled-components";

import type { IconName } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { IDropDownItem } from "@similarweb/ui-components";
import {
    IconButton,
    PopupClickContainer,
    ScrollArea,
    EllipsisDropdownItem,
    Autocomplete,
    DotsLoader,
    PlainTooltip,
} from "@similarweb/ui-components";

import {
    KeywordGeneratorToolPageModalContainer,
    KeywordGeneratorToolPageModalHeader,
    KeywordGeneratorToolPageModalBody,
    KeywordGeneratorToolPageModalFooter,
    ButtonContainer,
} from "app/pages/keyword-research/keyword-analysis/pages/keyword-generator/google-keyword-generator/styledComponents";
import { entities } from "app/common/keyword-research/serp-players/consts";
import { ECategoryType } from "app/common/services/categoryService.types";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { ProModal } from "pro-features/components/Modals/src/ProModal";

import { ModalTableSelectionNewGroup } from "./ModalTableSelectionNewGroup";

import { successArt } from "./successArt";
import { TableSelectionKeywordsWarning } from "./TableSelectionKeywordsWarning";
import { ETableSelectionNewGroupDropdownMode } from "./TableSelectionNewGroupDropdown";

import { HOOKS_ICON } from "app/constants/hooks";
import type { IAutocompleteStyledProps } from "pro-features/components/AutocompleteWebsites/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { WebsitePageSearchTypeNames } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import { openDefaultHooksModal } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";

interface ITableSelectionGroupWebsiteComponentProps {
    onNewListButtonClick: VoidFunction;
    onCancel?: VoidFunction;
    isCreateNewListModalOpen: boolean;
    isLoading?: boolean;
    newGroupNameLabel: string;
    appendTo: string;
    groupIconName?: IconName;
    groups: Array<{ id: string; text: string }>;
    onGroupClick?: (item: { id: string; text: string }) => void;
    onSubmit?: (name: string) => void;
    error?: boolean;
    errorMessage?: string;
    onListTypeSelect?: (type: ECategoryType) => void;
    selectedListType?: ECategoryType;
    mode: ETableSelectionNewGroupDropdownMode;
    newGroupItemText: string;
    currentGroup?: string;
    allItemsExistsMessage: string;
    maxItemsMessage: string;
    count: number;
    linkToGroup?: string;
    createGroupButtonKey?: string;
    addToGroupButtonKey?: string;
    searchListsPlaceholderKey?: string;
    customModalChildren?: React.ReactNode;
    isCreateNewListHooked?: boolean;
    keywordGapAnalysis?: string;
    keywordGapAnalysisTargetState?: string;
    selectedRows?: any[];
    initialIsVisible?: boolean;
    entity?: string;
}
const ADD_ICON = "add";
const TableSelectionGroupWebsiteComponentWrap = styled.div`
    display: flex;
    & button {
        margin-right: 10px;
    }
`;

const IconButtonDisabled = styled(IconButton)`
    border: none !important;
`;

const proModalStyles: IProModalCustomStyles = {
    content: {
        boxSizing: "border-box",
        width: "400px",
        padding: 0,
    },
};
const AutocompleteStyled = styled(Autocomplete)<IAutocompleteStyledProps>`
    width: 320px;
    .AutoCompleteContainer {
        border-radius: 0;
    }
    .input-container {
        border-radius: 3px 3px 0 0;
        height: 44px;
        padding-bottom: 8px;
        padding-top: 8px;
    }
    .input-container:after {
        content: "";
        width: 90%;
        position: absolute;
        bottom: 0;
        height: 1px;
        background-color: ${colorsPalettes.blue[400]};
    }
`;

const NoResultsWrapper = styled.div`
    padding: 44px 30px;
    text-align: center;
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

const ItemStyled = styled(EllipsisDropdownItem)<any>`
    padding: 0 18px;
    margin-bottom: 1px;
    position: relative;
    ${({ appendSeparator }) =>
        appendSeparator &&
        css`
            &:after {
                content: "";
                width: calc(100% - 16px);
                height: 1px;
                background-color: ${colorsPalettes.carbon[100]};
                position: absolute;
                bottom: 0;
                left: 50%;
                transform: translateX(-50%);
            }
        `}
    ${({ disableHover }) =>
        disableHover &&
        css`
            &:hover {
                background-color: inherit;
                cursor: default;
            }
        `}
`;
const KeywordGeneratorToolPageModalContainerWithPadding = styled(KeywordGeneratorToolPageModalContainer as any)`
    padding: 20px 16px;
`;

const DropDownItem = styled(EllipsisDropdownItem)<
    IDropDownItem & { iconName: string; iconSize: string; children?: ReactNode }
>`
    ${css`
        svg {
            width: 100%;
            height: 100%;
        }
    `}
`;

const AddItemButton = styled(ItemStyled)`
    position: relative;
    align-items: center;
    padding: 0 16px 4px 16px;
    cursor: pointer;
    background-color: ${colorsPalettes.carbon[0]};
    ${setFontVariant("input2", colorsPalettes.carbon[500])};
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 3px;
    &:hover {
        background-color: ${colorsPalettes.carbon[25]} !important;
        cursor: pointer;
        div {
            background-color: ${colorsPalettes.carbon[25]} !important;
            cursor: pointer;
        }
    }
`;

interface ITableSelectionGroupWebsiteComponentWrap {
    close: VoidFunction;
}
const TableSelectionGroupWebsiteComponentRenderFunction: ForwardRefRenderFunction<
    ITableSelectionGroupWebsiteComponentWrap,
    ITableSelectionGroupWebsiteComponentProps
> = (props, ref) => {
    const i18n = i18nFilter();
    const {
        onNewListButtonClick,
        isCreateNewListModalOpen,
        newGroupNameLabel,
        onCancel: onCancelProp = _.noop,
        appendTo,
        groups,
        groupIconName,
        onGroupClick = _.noop,
        isLoading = false,
        onSubmit: onSubmitProp = _.noop,
        error,
        errorMessage,
        selectedListType = ECategoryType.GENERAL_LIST,
        mode,
        currentGroup,
        newGroupItemText,
        maxItemsMessage,
        allItemsExistsMessage,
        count,
        linkToGroup,
        createGroupButtonKey = "table.selection.create.group",
        addToGroupButtonKey = "table.selection.add.to.list",
        searchListsPlaceholderKey = "table.selection.autocomplete.placeholder",
        customModalChildren,
        keywordGapAnalysis,
        keywordGapAnalysisTargetState = "organicsearch_websites_v2_traffic_keywords",
        isCreateNewListHooked,
        selectedRows,
        initialIsVisible,
        entity,
    } = props;
    const { params } = useCurrentStateAndParams();
    const isUrl = entity === entities.URL;
    const [groupName, setGroupName] = useState("");
    // @ts-ignore FIXME: strict migration - closePopup is from ui-components PopupClickContainer ref
    const popupEl = useRef<FixmeType>(null);
    const onCancel = () => {
        setGroupName("");
        onCancelProp();
    };
    useImperativeHandle(ref, () => ({
        close: () => {
            popupEl?.current?.closePopup();
        },
    }));
    const onGroupNameChange = (groupName: string) => {
        setGroupName(groupName);
    };
    const onToggle = (isOpen: boolean) => {
        allTrackers.dispatchTrackEvent("Drop Down", isOpen ? "open" : "close", "Website List");
    };

    const onItemClick = (item: FixmeType) => () => {
        onGroupClick(item);
    };
    const onSubmit = () => {
        onSubmitProp(groupName);
    };
    const handleCustomIndustry = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Custom Industry button");
    };

    const handleKeywordGapAnalysisClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Keyword Gap Analysis");
        const domainList = (selectedRows ?? []).map((item: FixmeType) => item.Domain).slice(0, 5);
        const urls: IChosenItemParams = domainList.map((item) => ({
            url: item,
            searchType: isUrl ? WebsitePageSearchTypeNames.ExactUrl : WebsitePageSearchTypeNames.Domain,
        }));

        const pageFilter = transformSiteListToParam(urls);
        const keyArr = urls.map(({ url }) => {
            const { domain, subdomain } = getUrlComponents(url);
            return getDomainWithSubdomain(domain ?? "", subdomain ?? "");
        });
        const key = Array.from(new Set(keyArr)).join();

        proNavigator.go(keywordGapAnalysisTargetState, {
            ...params,
            pageFilter,
            key,
        });
    };
    const itemCreator = (item: FixmeType, index: number) => {
        return (
            <DropDownItem
                key={`item-${index}`}
                id={item.id}
                // @ts-ignore FIXME: strict migration - groupIconName possibly undefined — consumer expects IconName
                iconName={groupIconName}
                iconSize="sm"
                onClick={onItemClick(item)}
            >
                {item.text}
            </DropDownItem>
        );
    };
    const ScrollAreaWrap = ({ children, ...rest }: { children: ReactNode; [key: string]: unknown }) => {
        return (
            <ScrollArea
                style={{
                    maxHeight: 200,
                    minHeight: 0,
                }}
                verticalScrollbarStyle={{ borderRadius: 5 }}
                horizontal={false}
                smoothScrolling={true}
                minScrollSize={48}
                {...rest}
            >
                {children}
            </ScrollArea>
        );
    };

    const getAutocompleteData = (query: string) => {
        if (!query) {
            return groups;
        } else {
            const normalizedQuery = query.trim().toLowerCase();
            const filteredGroups = groups.filter((group) => group.text.trim().toLowerCase().includes(normalizedQuery));

            return filteredGroups;
        }
    };

    const renderAutocompleteItems = ({ selectedItemId, listItems }: FixmeType) => {
        if (!listItems.length) {
            return <NoResultsWrapper>{i18n("table.selection.custom.industry.list.no.results")}</NoResultsWrapper>;
        }
        const componentListItems = listItems?.map(itemCreator);

        return <ScrollAreaWrap>{componentListItems}</ScrollAreaWrap>;
    };

    const getFirstItem = () => {
        switch (mode) {
            case ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS:
                return (
                    <ItemStyled
                        disableHover={true}
                        appendSeparator={true}
                        key="item-group"
                        iconName={groupIconName}
                        iconSize="sm"
                    >
                        {currentGroup}
                    </ItemStyled>
                );
            case ETableSelectionNewGroupDropdownMode.MAX_ITEMS:
                return (
                    <ItemStyled
                        disableHover={true}
                        appendSeparator={true}
                        key="item-group"
                        iconName={groupIconName}
                        iconSize="sm"
                    >
                        {i18n(newGroupItemText)}
                    </ItemStyled>
                );
            case ETableSelectionNewGroupDropdownMode.GROUP_LIST:
                return (
                    <>
                        {!isCreateNewListHooked ? (
                            <AddItemButton
                                disableHover={true}
                                appendSeparator={true}
                                key="item-group"
                                iconName={ADD_ICON}
                                iconSize="sm"
                                onClick={onNewListButtonClick}
                                iconColor={colorsPalettes.carbon[200]}
                            >
                                {i18n(createGroupButtonKey)}
                            </AddItemButton>
                        ) : null}
                    </>
                );
        }
    };
    const getContent = () => {
        switch (mode) {
            case ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS:
                return [
                    <TableSelectionKeywordsWarning
                        key="all-items-exist"
                        onCancel={onCancel}
                        message={i18n(allItemsExistsMessage)}
                    />,
                ];
            case ETableSelectionNewGroupDropdownMode.MAX_ITEMS:
                return [
                    <TableSelectionKeywordsWarning
                        key="max-items"
                        onCancel={onCancel}
                        message={i18n(maxItemsMessage, {
                            count: count.toString(),
                        })}
                    />,
                ];
            case ETableSelectionNewGroupDropdownMode.GROUP_LIST:
                return (
                    <AutocompleteStyled
                        className="AutocompleteKeywordGroupsAddToList"
                        getListItems={getAutocompleteData}
                        renderItems={renderAutocompleteItems}
                        loadingComponent={<DotsLoader />}
                        autoFocus={true}
                        floating={true}
                        debounce={250}
                        preventTruncateUnlessForced={true}
                        placeholder={translate(searchListsPlaceholderKey)}
                    />
                );
        }
    };
    const getPopupContent = () => {
        const firstItem = getFirstItem();
        const content = getContent();
        const isWarningMode = mode !== ETableSelectionNewGroupDropdownMode.GROUP_LIST;
        return (
            <div
                className={classNames("popup-group-list-content-frame", {
                    "popup-group-list-content-frame--clipped": isWarningMode,
                })}
            >
                {firstItem}
                {content}
            </div>
        );
    };
    const isDisabled = count === 0;
    const actionButtonsType = isDisabled ? "outlined" : "invertedTransparent";

    return (
        <TableSelectionGroupWebsiteComponentWrap>
            {isCreateNewListHooked && !isUrl ? (
                <IconButton
                    type={actionButtonsType}
                    onClick={() => openDefaultHooksModal()}
                    isDisabled={isDisabled}
                    iconName={HOOKS_ICON}
                >
                    {i18n("table.selection.create.group.hook")}
                </IconButton>
            ) : null}
            {groups.length === 0 && !isCreateNewListHooked && !isDisabled ? (
                <IconButton
                    type={actionButtonsType}
                    isDisabled={isDisabled}
                    iconName={ADD_ICON}
                    iconSize="xs"
                    onClick={onNewListButtonClick}
                >
                    {i18n(addToGroupButtonKey)}
                </IconButton>
            ) : null}
            {isDisabled && !isUrl ? (
                <IconButtonDisabled
                    type={actionButtonsType}
                    isDisabled={true}
                    iconName={ADD_ICON}
                    iconSize="xs"
                    onClick={handleCustomIndustry}
                >
                    {i18n(isCreateNewListHooked ? "table.selection.add.to.group.hook" : addToGroupButtonKey)}
                </IconButtonDisabled>
            ) : null}
            {groups.length > 0 && !isDisabled && !isUrl ? (
                <PopupClickContainer
                    content={getPopupContent}
                    config={{
                        height: isCreateNewListHooked ? 253 : 308,
                        placement: "bottom-left",
                        cssClassContainer: classNames(
                            "DropdownContent-container",
                            "Popup-content-for-group-list-dropdown",
                            "Popup-content--pro-dropdown",
                        ),

                        onToggle,
                    }}
                    appendTo="body"
                    ref={popupEl}
                >
                    <div>
                        <IconButton
                            type={actionButtonsType}
                            iconName={ADD_ICON}
                            iconSize="xs"
                            onClick={handleCustomIndustry}
                        >
                            {i18n(isCreateNewListHooked ? "table.selection.add.to.group.hook" : addToGroupButtonKey)}
                        </IconButton>
                    </div>
                </PopupClickContainer>
            ) : null}
            {keywordGapAnalysis ? (
                <PlainTooltip
                    placement="top"
                    tooltipContent={i18n("table.selection.keyword.gap.analysis.button.tooltip")}
                    enabled={!isDisabled}
                >
                    <span>
                        <IconButton
                            iconName="arrow-right"
                            onClick={handleKeywordGapAnalysisClick}
                            type={isDisabled ? "flat" : "primary"}
                            placement={"right"}
                            isDisabled={isDisabled}
                        >
                            {i18n(keywordGapAnalysis)}
                        </IconButton>
                    </span>
                </PlainTooltip>
            ) : null}

            <ProModal
                isOpen={isCreateNewListModalOpen}
                customStyles={proModalStyles}
                onCloseClick={onCancel}
                showCloseIcon={false}
            >
                {customModalChildren ? (
                    customModalChildren
                ) : linkToGroup ? (
                    <KeywordGeneratorToolPageModalContainerWithPadding>
                        <KeywordGeneratorToolPageModalHeader>
                            {i18n("table.selection.newgroup.websites.modal.success.title")}
                        </KeywordGeneratorToolPageModalHeader>
                        <KeywordGeneratorToolPageModalBody>
                            <svg dangerouslySetInnerHTML={{ __html: successArt }} />
                        </KeywordGeneratorToolPageModalBody>
                        <KeywordGeneratorToolPageModalFooter>
                            <a href={linkToGroup}>
                                <ButtonContainer
                                    type="primary"
                                    label={i18n("table.selection.newgroup.websites.modal.success.visit")}
                                />
                            </a>
                            <ButtonContainer
                                onClick={onCancel}
                                type="flat"
                                label={i18n("table.selection.newgroup.websites.modal.success.cancel")}
                            />
                        </KeywordGeneratorToolPageModalFooter>
                    </KeywordGeneratorToolPageModalContainerWithPadding>
                ) : (
                    <ModalTableSelectionNewGroup
                        title={i18n("table.selection.newgroup.websites.modal.title")}
                        subTitle={newGroupNameLabel}
                        placeholder={i18n("table.selection.newgroup.websites.placeholder")}
                        onCancel={onCancel}
                        onSubmit={onSubmit}
                        submitButtonDisabled={groupName.length === 0 || isLoading}
                        error={error}
                        errorMessage={errorMessage}
                        onGroupNameChange={onGroupNameChange}
                        isLoading={isLoading}
                        groupType="website"
                        selectedListType={selectedListType}
                    />
                )}
            </ProModal>
        </TableSelectionGroupWebsiteComponentWrap>
    );
};
export const TableSelectionGroupWebsiteComponent = forwardRef(TableSelectionGroupWebsiteComponentRenderFunction);
